import { useEffect, useMemo, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { useBetActions } from 'src/store/bet'
import { useSocialActions } from 'src/store/social'
import {
  useGameActions,
  useGetCurrentGameId,
  useGetCurrentGame,
  useGetGameConfig
} from 'src/store/game'
import { isKnownGameConfig } from 'src/types/domain/GameConfig'
import { usePlayerActions } from 'src/store/player'
import { useStreakActions } from 'src/store/streak'
import { useUIActions, useGetIsTracksPanelOpen } from 'src/store/ui'
import { isMarbleGame } from 'src/types/domain/Game'
import {
  handleCrashOccurred,
  handleGameConfig,
  handleMarbleCreated,
  handleMarbleDeleted,
  handleMarbleUpdated,
  handleRaceConfigurationCreated,
  handleRaceConfigurationUpdated,
  handleRaceCreated,
  handleRaceEventCreated,
  handleRaceEventUpdated,
  handleRaceUpdated,
  handleLightningRevealed,
  handleSocialBetReceived,
  handleRoundSnapshot,
  handleSocialBetWon,
  handleSocialBetCashedOut,
  handleSocialStreams,
  type Actions
} from 'src/utils/websocketEventHandlers'
import {
  useGetSocket,
  useGetGameChannel,
  useGetRaceChannel,
  useGetSocialChannel,
  useSocketActions
} from 'src/store/socket'
import { setupChannelListeners } from 'src/utils/setupChannelListeners'
import { useSocialStreamActions } from 'src/store/socialStream'
import { useDailyCheckInActions } from 'src/features/dailyCheckIn'
import { usePlayerBalance } from './usePlayerBalance'

export const useGameListeners = (isGameMismatch = false) => {
  const socketActions = useSocketActions()
  const socket = useGetSocket()
  const gameChannel = useGetGameChannel()
  const raceChannel = useGetRaceChannel()
  const socialChannel = useGetSocialChannel()
  const gameId = useGetCurrentGameId()
  const game = useGetCurrentGame()
  const isTracksPanelOpen = useGetIsTracksPanelOpen()
  const betActions = useBetActions()
  const gameActions = useGameActions()
  const socialActions = useSocialActions()
  const socialStreamActions = useSocialStreamActions()
  const playerActions = usePlayerActions()
  const streakActions = useStreakActions()
  const uiActions = useUIActions()
  const dailyCheckinActions = useDailyCheckInActions()
  const { updateBalance } = usePlayerBalance()
  const router = useRouter()
  const gameConfig = useGetGameConfig()
  const isChipPlacement =
    isKnownGameConfig(gameConfig) && gameConfig.bettingMode === 'chip-placement'

  // Backstop for the chip-placement pre-reveal payout strip. The
  // primary trigger is `handleLightningRevealed` (fires when the
  // server pushes the `lightning:revealed` socket event); this watcher
  // catches the page-reload case where lightning is already revealed
  // by the time we mount and the initial currentRace state already
  // carries the boost data — no `lightning:revealed` event will fire
  // again, but we still need to lift the strip on the cached bets.
  // Safe on classic games because we gate on `isChipPlacement` (only
  // chip-placement bets were stripped to begin with). The store-side
  // `applyLightningBoost` is idempotent per-round so calling this
  // alongside the live `lightning:revealed` handler can't double-boost.
  const currentRace = isMarbleGame(game) ? game.races[0] : undefined
  const lightningRoundId =
    currentRace?.isLightningRound &&
    currentRace.lightningBoostedItems &&
    currentRace.lightningBoostedItems.length > 0 &&
    currentRace.lightningBoostPercent &&
    currentRace.lightningBoostPercent > 0
      ? currentRace.uuid
      : undefined
  useEffect(() => {
    if (!isChipPlacement) return
    if (!lightningRoundId || !currentRace) return
    const items = currentRace.lightningBoostedItems ?? []
    const percent = currentRace.lightningBoostPercent ?? 0
    if (items.length === 0 || percent <= 0) return
    betActions.applyLightningBoost(lightningRoundId, items, percent)
    socialActions.applyLightningBoost(lightningRoundId, items, percent)
  }, [isChipPlacement, lightningRoundId, currentRace, betActions, socialActions])

  const hasSocket = Boolean(socket)

  const isRaceChannelJoined = raceChannel?.state === 'joined'
  const isGameChannelJoined = gameChannel?.state === 'joined'
  const isSocialChannelJoined = socialChannel?.state === 'joined'

  const actions: Actions = useMemo(
    () => ({
      bet: betActions,
      player: playerActions,
      ui: uiActions,
      game: gameActions,
      social: socialActions,
      socket: socketActions,
      socialStream: socialStreamActions,
      dailyCheckin: dailyCheckinActions,
      streak: streakActions
    }),
    [
      betActions,
      playerActions,
      uiActions,
      gameActions,
      socialActions,
      socketActions,
      socialStreamActions,
      dailyCheckinActions,
      streakActions
    ]
  )

  // Join Game channel
  useEffect(() => {
    if (!hasSocket || !gameId || isGameMismatch || isGameChannelJoined) return
    void actions.socket.joinGameChannel(gameId)
  }, [hasSocket, actions.socket, gameId, isGameChannelJoined, isGameMismatch])

  // Attach Game channel event listeners
  useEffect(() => {
    if (!isGameChannelJoined || isGameMismatch) return

    const cleanup = setupChannelListeners(gameChannel, [
      { event: 'game-config', handler: handleGameConfig(actions, router) },
      { event: 'social-streams', handler: handleSocialStreams(actions) },
      { event: 'crash:occurred', handler: handleCrashOccurred(actions, gameId) }
    ])

    return () => {
      if (cleanup) cleanup()
      actions.socket.leaveGameChannel()
    }
  }, [actions, isGameChannelJoined, isGameMismatch, gameChannel, router, gameId])

  // Join Social channel — TOPIC-AWARE (fixes the long-standing "social feed
  // empty after switching games until refresh"). The old guard only asked
  // "is A social channel joined?", and nothing has left the previous game's
  // channel on switch since #522 removed leaveSocialChannel from the cleanup
  // — so after A→B the client stayed subscribed to game A's social-events
  // forever: game B's round:snapshot is only pushed on JOIN, and B's live
  // bet broadcasts go to a topic we were never on. Empty feed until a full
  // refresh re-joined correctly. Now the guard also requires the joined
  // channel to be THIS game's topic; joinSocialChannel itself leaves the
  // previous channel first (src/store/socket.ts), so a switch cleanly
  // re-subscribes and receives the fresh snapshot.
  const wantedSocialTopic = `realtime:public:game-${gameId}:social-events`
  const isOnWantedSocialTopic =
    isSocialChannelJoined && socialChannel?.topic === wantedSocialTopic

  // The social listeners are bound at channel CONSTRUCTION, not after the join
  // resolves. The server queues `:after_social_join` from inside the join
  // handler, so `round:snapshot` — the round's entire pre-join bet history —
  // lands immediately after the join reply, and phoenix-js drops events with no
  // binding. A React effect gated on `isSocialChannelJoined` is one commit too
  // late, so anyone opening the game, switching games, refreshing or
  // reconnecting mid-round used to start on an empty feed. Nothing ever
  // re-requests the snapshot, so the loss was permanent for that round.
  //
  // The binder runs ONCE per channel, so it must not close over `actions`
  // directly — reading through a ref defers the lookup to event time and keeps a
  // long-lived channel from dispatching into a stale set.
  const actionsRef = useRef(actions)
  actionsRef.current = actions

  useEffect(() => {
    if (!hasSocket || !gameId || isOnWantedSocialTopic) return

    void actions.socket.joinSocialChannel(gameId, (channel) => {
      setupChannelListeners(channel, [
        { event: 'bet', handler: (data) => handleSocialBetReceived(actionsRef.current)(data) },
        { event: 'round:snapshot', handler: (data) => handleRoundSnapshot(actionsRef.current)(data) },
        { event: 'bet:won', handler: (data) => handleSocialBetWon(actionsRef.current)(data) },
        {
          event: 'bet:cashed_out',
          handler: (data) => handleSocialBetCashedOut(actionsRef.current)(data)
        }
      ])
    })
  }, [hasSocket, isOnWantedSocialTopic, actions.socket, gameId])

  // Join Race channel
  useEffect(() => {
    if (!hasSocket || isRaceChannelJoined) return

    void actions.socket.joinRaceChannel()
  }, [hasSocket, isRaceChannelJoined, actions.socket])

  // Attach Race channel listeners
  useEffect(() => {
    if (!isRaceChannelJoined) return

    const cleanup = setupChannelListeners(raceChannel, [
      {
        event: 'race-configuration:created',
        handler: handleRaceConfigurationCreated(actions, gameId)
      },
      {
        event: 'race-configuration:updated',
        handler: handleRaceConfigurationUpdated(actions, gameId)
      },
      {
        event: 'race:created',
        handler: handleRaceCreated(actions, isTracksPanelOpen ? undefined : gameId)
      },
      {
        event: 'race:updated',
        handler: handleRaceUpdated(actions, isTracksPanelOpen ? undefined : gameId)
      },
      {
        event: 'race-event:created',
        handler: handleRaceEventCreated(
          actions,
          isTracksPanelOpen ? undefined : gameId,
          updateBalance
        )
      },
      {
        event: 'race-event:updated',
        handler: handleRaceEventUpdated(actions, isTracksPanelOpen ? undefined : gameId)
      },
      // Chip-placement / Evolution-style lightning reveal — fires when
      // betting closes for a lightning round. Backend sends only after
      // the gate flips, so the chip-placement panel reads the boosted
      // items + percent for the first time here and the existing
      // strike-animation derives from the local race state update.
      {
        event: 'lightning:revealed',
        handler: handleLightningRevealed(actions, isTracksPanelOpen ? undefined : gameId)
      },
      { event: 'marble:created', handler: handleMarbleCreated(actions, gameId) },
      { event: 'marble:updated', handler: handleMarbleUpdated(actions, gameId) },
      { event: 'marble:deleted', handler: handleMarbleDeleted(actions, gameId) }
    ])

    return () => {
      if (cleanup) cleanup()
    }
  }, [isRaceChannelJoined, raceChannel, actions, gameId, isTracksPanelOpen, updateBalance])
}
